import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Check,
  Loader2,
  Pencil,
  UserPlus,
  Upload,
  UploadCloud,
  Lightbulb,
} from "lucide-react"; // Added icons
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../firebase";
import featureGatingService from "../services/featureGatingService";
import { CASE_STATUSES, CASE_STAGES, CASE_PRIORITIES, getStatusCollection } from "../utils/caseStatus";

import SelectMenu from "../components/crm/SelectMenu";
const initialCaseData = {
  status: "",
  stage: "",
  priority: "",
  caseNumber: "",
  caseType: "",
  caseYear: "",
  caseFiledDate: null,
  nextHearingDate: null,

  previousHearingDate: null,
  appearingFor: "",
  court: "",
  specificCourt: "",
  customCourt: "",
  petitionerName: "",
  petitionerRelation: "",
  petitionerRelationName: "",
  respondentName: "",
  respondentRelation: "",
  respondentRelationName: "",
  partyContactNumber: "",
  caseNotes: "",
  files: [],
  allPetitioners: [],
  allRespondents: [],
  petitionerAdvocate: "",
  respondentAdvocate: "",
  allPetitionerAdvocates: [],
  allRespondentAdvocates: [],
  authorityDepartment: "",
  authorityDepartmentOther: "",
  properties: "",
  area: "",
  village: "",
  stayOrderDate: null,
  counterAffidavitStatus: "",
  counterAppealFiled: "",
  prayers: "",
  supremeCourtStage: "",
  supremeCourtCategory: "",
  supremeCourtSection: "",
};

const AUTHORITY_DEPARTMENTS = [
  "Residential",
  "Institutional",
  "IT",
  "HRD",
  "Water",
  "Urban Services",
  "Land",
  "Builders",
  "Group Housing",
  "Sewar",
  "Health",
  "University",
  "Finance",
  "Commercial",
  "6% Abadi Plot",
  "Others",
];

const COUNTER_AFFIDAVIT_STATUSES = [
  "Not Initiated",
  "Notice Received - Awaiting Instructions",
  "Para-wise Comments (PWC) Requested",
  "PWC Received from Department",
  "Drafting Under Preparation",
  "Draft Sent for Department Approval",
  "Vetted & Under Execution",
  "Filed (Physical/e-Filing)",
  "Served on Applicant/Parties",
  "Rejoinder Received",
];

const CustomSelect = ({ value, onChange, options, placeholder = "Select an option" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white text-slate-800 p-3 rounded-xl border border-slate-200 hover:border-slate-300 text-left flex items-center justify-between transition-colors"
      >
        <span className={`truncate ${!value ? "text-slate-400" : ""}`}>
          {value || placeholder}
        </span>
        <ChevronDown className={`ml-2 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} size={20} />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 text-left text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 text-sm"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const HIGH_COURTS = [
  "Allahabad High Court",
  "Andhra Pradesh High Court",
  "Bombay High Court",
  "Calcutta High Court",
  "Chhattisgarh High Court",
  "Delhi High Court",
  "Gauhati High Court",
  "Gujarat High Court",
  "Himachal Pradesh High Court",
  "Jammu & Kashmir and Ladakh High Court",
  "Jharkhand High Court",
  "Karnataka High Court",
  "Kerala High Court",
  "Madhya Pradesh High Court",
  "Madras High Court",
  "Manipur High Court",
  "Meghalaya High Court",
  "Orissa High Court",
  "Patna High Court",
  "Punjab and Haryana High Court",
  "Rajasthan High Court",
  "Sikkim High Court",
  "Telangana High Court",
  "Tripura High Court",
  "Uttarakhand High Court",
];

const NCLT_BENCHES = [
  "NCLT Principal Bench, New Delhi",
  "NCLT Ahmedabad Bench",
  "NCLT Allahabad Bench",
  "NCLT Amaravati Bench",
  "NCLT Bengaluru Bench",
  "NCLT Chandigarh Bench",
  "NCLT Chennai Bench",
  "NCLT Cuttack Bench",
  "NCLT Guwahati Bench",
  "NCLT Hyderabad Bench",
  "NCLT Indore Bench",
  "NCLT Jaipur Bench",
  "NCLT Kochi Bench",
  "NCLT Kolkata Bench",
  "NCLT Mumbai Bench",
  "NCLT Pune Bench",
];

const RERA_AUTHORITIES = [
  "RERA Andhra Pradesh",
  "RERA Bihar",
  "RERA Chhattisgarh",
  "RERA Delhi",
  "RERA Goa",
  "RERA Gujarat",
  "RERA Haryana",
  "RERA Himachal Pradesh",
  "RERA Jharkhand",
  "RERA Karnataka",
  "RERA Kerala",
  "RERA Madhya Pradesh",
  "RERA Maharashtra",
  "RERA Odisha",
  "RERA Punjab",
  "RERA Rajasthan",
  "RERA Tamil Nadu",
  "RERA Telangana",
  "RERA Uttar Pradesh",
  "RERA Uttarakhand",
  "RERA West Bengal",
];

// Relation options used for petitioner/respondent
const RELATION_OPTIONS = ["S/o", "D/o", "W/o"];

// Modal to pick S/o D/o W/o and enter the associated name
const RelationDialog = ({ isOpen, onClose, title, relation, relationName, onSave, namePlaceholder = "Enter Name" }) => {
  const [localRelation, setLocalRelation] = useState(relation || "");
  const [localName, setLocalName] = useState(relationName || "");

  useEffect(() => {
    if (isOpen) {
      setLocalRelation(relation || "");
      setLocalName(relationName || "");
    }
  }, [isOpen, relation, relationName]);

  if (!isOpen) return null;

  const handleNameChange = (e) => {
    const val = e.target.value;
    if (/[^a-zA-Z\s.]/.test(val)) return;
    setLocalName(val);
  };

  const handleSave = () => {
    if (!localRelation) {
      onSave({ relation: "", relationName: "" });
    } else {
      onSave({ relation: localRelation, relationName: (localName || "").trim() });
    }
    onClose();
  };

  const handleClear = () => {
    setLocalRelation("");
    setLocalName("");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors" type="button">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-slate-600 text-sm mb-2 block">Select relation type</label>
            <div className="flex flex-wrap gap-2">
              {RELATION_OPTIONS.map((opt) => {
                const active = localRelation === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setLocalRelation(active ? "" : opt)}
                    className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                      active
                        ? "bg-yellow-500 text-gray-900 border-yellow-500"
                        : "bg-white text-slate-600 border-slate-300 hover:border-yellow-500 hover:text-yellow-700"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-2">S/o – Son of · D/o – Daughter of · W/o – Wife of</p>
          </div>
          {localRelation && (
            <div>
              <label className="text-slate-600 text-sm mb-2 block">{localRelation} name</label>
              <input
                type="text"
                value={localName}
                onChange={handleNameChange}
                className="w-full bg-white text-slate-800 p-3 rounded-xl border border-slate-200 text-sm"
                placeholder={namePlaceholder}
                autoFocus
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-200">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-slate-400 hover:text-red-500 transition-colors"
          >
            Clear
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-xl transition-colors text-sm"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact badge shown below the name input. Click to open dialog. Shows "+ Add Relation" when empty.
const RelationBadge = ({ relation, relationName, onOpen, onClear }) => {
  const hasValue = relation && relationName;
  if (!hasValue) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="text-sm text-yellow-700 hover:text-yellow-700 transition-colors whitespace-nowrap"
      >
        + Add Relation
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-50 border border-yellow-300 text-xs text-yellow-700 max-w-full">
      <span className="font-semibold">{relation}</span>
      <span className="text-yellow-600 truncate">{relationName}</span>
      <button
        type="button"
        onClick={onOpen}
        title="Edit relation"
        className="text-yellow-600 hover:text-yellow-700 transition-colors flex-shrink-0"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onClear}
        title="Remove relation"
        className="text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// Add More Modal Component
const AddMoreModal = ({ isOpen, onClose, title, items, setItems, supportsRelation = false }) => {
  const makeEmpty = () => (supportsRelation ? { name: "", relation: "", relationName: "" } : { name: "" });

  const normalize = (item) => (supportsRelation
    ? { name: item?.name || "", relation: item?.relation || "", relationName: item?.relationName || "" }
    : { name: item?.name || "" });

  const [localItems, setLocalItems] = useState(items.length > 0 ? items.map(normalize) : [makeEmpty()]);
  const [relationDialogIndex, setRelationDialogIndex] = useState(-1);

  useEffect(() => {
    setLocalItems(items.length > 0 ? items.map(normalize) : [makeEmpty()]);
  }, [items, isOpen]);

  const addNewItem = () => {
    setLocalItems([...localItems, makeEmpty()]);
  };

  const removeItem = (index) => {
    setLocalItems(localItems.filter((_, i) => i !== index));
  };

  const updateItem = (index, value) => {
    if (/[^a-zA-Z\s]/.test(value)) return;
    const updated = [...localItems];
    updated[index] = { ...updated[index], name: value };
    setLocalItems(updated);
  };

  const setItemRelation = (index, relation, relationName) => {
    const updated = [...localItems];
    updated[index] = { ...updated[index], relation: relation || "", relationName: relationName || "" };
    setLocalItems(updated);
  };

  const handleSave = () => {
    const filtered = localItems
      .filter(item => item.name.trim() !== "")
      .map(item => supportsRelation
        ? { name: item.name.trim(), relation: item.relation || "", relationName: (item.relationName || "").trim() }
        : { name: item.name.trim() });
    setItems(filtered);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {localItems.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(index, e.target.value)}
                  className="flex-1 bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                  placeholder={`Enter Name ${index + 1}`}
                />
                {localItems.length > 1 && (
                  <button
                    onClick={() => removeItem(index)}
                    className="p-2 text-red-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 pl-3">
                {index === localItems.length - 1 ? (
                  <button
                    onClick={addNewItem}
                    className="flex items-center gap-1 text-yellow-700 hover:text-yellow-700 transition-colors text-xs"
                    title="Add more"
                    type="button"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span>Add more</span>
                  </button>
                ) : (
                  <span />
                )}
                {supportsRelation && (
                  <RelationBadge
                    relation={item.relation || ""}
                    relationName={item.relationName || ""}
                    onOpen={() => setRelationDialogIndex(index)}
                    onClear={() => setItemRelation(index, "", "")}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center p-6 border-t border-slate-200">
          <button
            onClick={handleSave}
            className="px-8 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-xl transition-colors"
          >
            Save
          </button>
        </div>
      </div>
      {supportsRelation && relationDialogIndex >= 0 && (
        <RelationDialog
          isOpen={relationDialogIndex >= 0}
          onClose={() => setRelationDialogIndex(-1)}
          title={`Set Relation${localItems[relationDialogIndex]?.name ? ` — ${localItems[relationDialogIndex].name}` : ""}`}
          relation={localItems[relationDialogIndex]?.relation || ""}
          relationName={localItems[relationDialogIndex]?.relationName || ""}
          onSave={({ relation, relationName }) => setItemRelation(relationDialogIndex, relation, relationName)}
          namePlaceholder="Enter Name"
        />
      )}
    </div>
  );
};

// Custom Checkbox component to mimic CheckboxListTile look/feel
const CustomCheckbox = ({ label, checked, onChange, disabled = false }) => {
  return (
    <label
      className={`flex items-center space-x-3 cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="form-checkbox h-5 w-5 text-yellow-700 bg-white border-slate-300 rounded focus:ring-yellow-500 accent-yellow-500" // Style the checkbox
      />
      <span className="text-slate-700">{label}</span>
    </label>
  );
};

function AddCase() {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalUser } = useAuth(); // Use auth context
  const [draftId, setDraftId] = useState(location.state?.draftId || null);
  const [caseData, setCaseData] = useState(() => {
    // Load from draft opened via Drafts page
    if (location.state?.draftData) {
      const d = { ...location.state.draftData };
      if (d.caseFiledDate) d.caseFiledDate = new Date(d.caseFiledDate);
      if (d.previousHearingDate) d.previousHearingDate = new Date(d.previousHearingDate);
      if (d.nextHearingDate) d.nextHearingDate = new Date(d.nextHearingDate);
      if (d.stayOrderDate) d.stayOrderDate = new Date(d.stayOrderDate);
      return d;
    }
    if (location.state?.caseData) {
      return location.state.caseData;
    }
    const savedDraft = localStorage.getItem("caseDraft");
    if (savedDraft) {
      const parsedData = JSON.parse(savedDraft);
      if (parsedData.caseFiledDate) {
        parsedData.caseFiledDate = new Date(parsedData.caseFiledDate);
      }
      if (parsedData.previousHearingDate) {
        parsedData.previousHearingDate = new Date(
          parsedData.previousHearingDate
        );
      }
      if (parsedData.nextHearingDate) {
        parsedData.nextHearingDate = new Date(parsedData.nextHearingDate);
      }
      if (parsedData.stayOrderDate) {
        parsedData.stayOrderDate = new Date(parsedData.stayOrderDate);
      }
      return parsedData;
    }
    return initialCaseData;
  });
  const fileInputRef = useRef(null);

  // --- Team Sharing State ---
  const [shareWithTeam, setShareWithTeam] = useState(false);
  const [userTeams, setUserTeams] = useState([]); // {id, teamName}[]
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [memberSelectionType, setMemberSelectionType] = useState("All Members"); // 'All Members' or 'Specific Members'
  const [teamMembersDetails, setTeamMembersDetails] = useState([]); // {uid, name}[]
  const [selectedMemberUids, setSelectedMemberUids] = useState([]); // string[]
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [teamError, setTeamError] = useState(null);
  // --- End Team Sharing State ---

  // Duplicate case validation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasDuplicateCaseError, setHasDuplicateCaseError] = useState(false);
  const [duplicateCaseErrorMessage, setDuplicateCaseErrorMessage] = useState('');
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  // Add More Modal states
  const [showPetitionerModal, setShowPetitionerModal] = useState(false);
  const [showRespondentModal, setShowRespondentModal] = useState(false);
  const [showPetitionerAdvocateModal, setShowPetitionerAdvocateModal] = useState(false);
  const [showRespondentAdvocateModal, setShowRespondentAdvocateModal] = useState(false);
  const [showPetitionerRelationDialog, setShowPetitionerRelationDialog] = useState(false);
  const [showRespondentRelationDialog, setShowRespondentRelationDialog] = useState(false);

  const [isDepartmentVerified, setIsDepartmentVerified] = useState(false);
  // Organisation groups (with case sharing enabled) the current user belongs to.
  const [userSharingGroups, setUserSharingGroups] = useState([]);

  useEffect(() => {
    const savedNotes = localStorage.getItem("caseNotes");
    if (savedNotes) {
      setCaseData((prev) => ({ ...prev, caseNotes: savedNotes }));
    }
  }, []);

  useEffect(() => {
    const fetchDeptVerification = async () => {
      if (!globalUser?.uid) {
        setIsDepartmentVerified(false);
        return;
      }
      try {
        const userCollectionName =
          localStorage.getItem("userType") === "client" ? "client" : "lawyers";
        const userDoc = await getDoc(doc(db, userCollectionName, globalUser.uid));
        setIsDepartmentVerified(
          userDoc.exists() ? Boolean(userDoc.data().departmentVerified) : false
        );
        setUserSharingGroups(
          userDoc.exists() && Array.isArray(userDoc.data().sharingGroups)
            ? userDoc.data().sharingGroups
            : []
        );
      } catch (error) {
        console.error("Error fetching department verification:", error);
        setIsDepartmentVerified(false);
        setUserSharingGroups([]);
      }
    };
    fetchDeptVerification();
  }, [globalUser]);

  // --- Define fetchTeams using useCallback ---
  const fetchTeams = useCallback(async () => {
    // Added check for globalUser here as well
    if (!globalUser?.uid) {
      console.log("fetchTeams: No user ID, skipping fetch.");
      
      return;
    }

    console.log("fetchTeams: Fetching teams for user:", globalUser.uid); // Debug log
    setLoadingTeams(true);
    setTeamError(null); // Clear previous errors
    try {
      const teamsQuery = query(
        collection(db, "teams"),
        where("ownerId", "==", globalUser.uid)
      );
      const querySnapshot = await getDocs(teamsQuery);
      const fetchedTeams = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log("fetchTeams: Fetched teams:", fetchedTeams); // Debug log
      setUserTeams(fetchedTeams);
    } catch (err) {
      console.error("Error fetching teams:", err);
      setTeamError("Failed to load your teams.");
      setUserTeams([]); // Clear teams on error
    } finally {
      setLoadingTeams(false);
    }
  }, [globalUser]); // Dependency: globalUser

  // --- useEffect to call fetchTeams on mount (or when fetchTeams changes) ---
  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]); // Now depends on the stable useCallback function

  // --- Fetch Team Members when a team is selected ---
  const fetchTeamMembers = useCallback(async (teamId) => {
    if (!teamId) {
      setTeamMembersDetails([]);
      return;
    }
    setLoadingTeamMembers(true);
    setTeamMembersDetails([]); // Clear previous members
    setTeamError(null);

    try {
      const teamDocRef = doc(db, "teams", teamId);
      const teamDocSnap = await getDoc(teamDocRef);

      if (teamDocSnap.exists()) {
        const memberIds = teamDocSnap.data().members || [];
        if (memberIds.length > 0) {
          const memberPromises = memberIds.map((id) =>
            getDoc(doc(db, "lawyers", id))
          ); // Assuming 'lawyers' collection
          const memberDocs = await Promise.all(memberPromises);
          const membersData = memberDocs
            .filter((docSnap) => docSnap.exists())
            .map((docSnap) => ({
              uid: docSnap.id,
              name: docSnap.data().name || `User ${docSnap.id.substring(0, 5)}`,
            })); // Get name, provide fallback
          setTeamMembersDetails(membersData);
        } else {
          setTeamMembersDetails([]); // Team exists but has no members
        }
      } else {
        setTeamError("Selected team not found.");
        setTeamMembersDetails([]);
      }
    } catch (err) {
      console.error("Error fetching team members:", err);
      setTeamError("Failed to load team members.");
      setTeamMembersDetails([]);
    } finally {
      setLoadingTeamMembers(false);
    }
  }, []); // No dependencies needed here as it's called explicitly

  useEffect(() => {
    if (selectedTeamId) {
      fetchTeamMembers(selectedTeamId);
    } else {
      setTeamMembersDetails([]); // Clear members if no team is selected
    }
  }, [selectedTeamId, fetchTeamMembers]);

  // Function to check for duplicate case numbers
  const checkDuplicateCaseNumber = async (caseNumber) => {
    if (!caseNumber.trim() || !globalUser?.uid) {
      setHasDuplicateCaseError(false);
      setDuplicateCaseErrorMessage('');
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      // Check in pending collection
      const pendingQuery = query(
        collection(db, 'pending'),
        where('owner', '==', globalUser.uid),
        where('caseNo', '==', caseNumber.trim())
      );
      const pendingSnapshot = await getDocs(pendingQuery);

      // Check in disposed collection
      const disposedQuery = query(
        collection(db, 'disposed'),
        where('owner', '==', globalUser.uid),
        where('caseNo', '==', caseNumber.trim())
      );
      const disposedSnapshot = await getDocs(disposedQuery);

      const hasDuplicate = !pendingSnapshot.empty || !disposedSnapshot.empty;

      setHasDuplicateCaseError(hasDuplicate);
      setDuplicateCaseErrorMessage(hasDuplicate ? 'Case number already exists' : '');
    } catch (error) {
      console.error('Error checking duplicate case number:', error);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "petitionerName" || name === "respondentName" || name === "petitionerAdvocate" || name === "respondentAdvocate") {
      if (/[^a-zA-Z\s]/.test(value)) return;
    }
    if (name === "partyContactNumber") {
      if (/[^0-9]/.test(value) || value.length > 10) return;
    }
    setCaseData((prev) => ({ ...prev, [name]: value }));

    // Clear duplicate error when user starts typing in case number field
    if (name === 'caseNumber' && hasDuplicateCaseError) {
      setHasDuplicateCaseError(false);
      setDuplicateCaseErrorMessage('');
    }

    if (name === "court") {
      setCaseData((prev) => ({ ...prev, customCourt: "", specificCourt: "" }));
    }
  };

  const handleCaseFiledDateChange = (date) => {
    setCaseData((prev) => ({ ...prev, caseFiledDate: date }));
  };

  const handlePreviousHearingDateChange = (date) => {
    if (date) {
      const todayCopy = new Date();
      todayCopy.setHours(23, 59, 59, 999);
      if (date > todayCopy) {
        toast.error("Previous hearing date cannot be in the future");
        return;
      }
    }
    setCaseData((prev) => ({ ...prev, previousHearingDate: date }));
  };

  const handleNextHearingDateChange = (date) => {
    if (date) {
      const todayCopy = new Date();
      todayCopy.setHours(0, 0, 0, 0);
      if (date < todayCopy) {
        toast.error("Next hearing date cannot be in the past");
        return;
      }
      if (caseData.previousHearingDate && date < caseData.previousHearingDate) {
        toast.error("Next hearing date cannot be before the previous hearing date");
        return;
      }
    }
    setCaseData((prev) => ({ ...prev, nextHearingDate: date }));
  };

  const handleFileUpload = async (e) => {
   
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const uploadPromises = filesArray.map(async (file) => {
        const storageRef = ref(
          storage,
          `case-files/${Date.now()}-${file.name}`
        );
        // Consider adding loading state here
        const toastId = toast.loading(`Uploading ${file.name}...`);
        try {
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          toast.success(`${file.name} uploaded successfully!`, { id: toastId });
          return url;
        } catch (error) {
          console.error("Upload Error:", error);
          toast.error(`Failed to upload ${file.name}`, { id: toastId });
          return null; // Indicate failure
        }
      });

      const urls = (await Promise.all(uploadPromises)).filter(
        (url) => url !== null
      ); // Filter out failed uploads
      setCaseData((prev) => ({ ...prev, files: [...prev.files, ...urls] }));
      // toast.success("Files processed."); // Removed redundant toast
    }
  };

  const handleSaveDraft = async () => {
    if (!globalUser) {
      toast.error("You must be logged in to save a draft.");
      return;
    }
    const draftPayload = {
      ...caseData,
      caseFiledDate: caseData.caseFiledDate ? caseData.caseFiledDate.toISOString() : null,
      previousHearingDate: caseData.previousHearingDate ? caseData.previousHearingDate.toISOString() : null,
      nextHearingDate: caseData.nextHearingDate ? caseData.nextHearingDate.toISOString() : null,
      stayOrderDate: caseData.stayOrderDate ? caseData.stayOrderDate.toISOString() : null,
      _teamSharingState: {
        shareWithTeam,
        selectedTeamId,
        memberSelectionType,
        selectedMemberUids,
      },
      userId: globalUser.uid,
      updatedAt: serverTimestamp(),
    };
    try {
      if (draftId) {
        await setDoc(doc(db, "drafts", draftId), draftPayload, { merge: true });
      } else {
        draftPayload.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, "drafts"), draftPayload);
        setDraftId(docRef.id);
      }
      toast.success("Draft saved successfully");
    } catch (err) {
      console.error("Error saving draft:", err);
      toast.error("Failed to save draft.");
    }
  };

  // Restore team sharing state from draft
  useEffect(() => {
    // Restore from Firebase draft opened via Drafts page
    if (location.state?.draftData?._teamSharingState) {
      const ts = location.state.draftData._teamSharingState;
      setShareWithTeam(ts.shareWithTeam || false);
      setSelectedTeamId(ts.selectedTeamId || "");
      setMemberSelectionType(ts.memberSelectionType || "All Members");
      setSelectedMemberUids(ts.selectedMemberUids || []);
      return;
    }
    const savedDraft = localStorage.getItem("caseDraft");
    if (savedDraft) {
      const parsedData = JSON.parse(savedDraft);
      if (parsedData._teamSharingState) {
        setShareWithTeam(parsedData._teamSharingState.shareWithTeam || false);
        setSelectedTeamId(parsedData._teamSharingState.selectedTeamId || "");
        setMemberSelectionType(
          parsedData._teamSharingState.memberSelectionType || "All Members"
        );
        setSelectedMemberUids(
          parsedData._teamSharingState.selectedMemberUids || []
        );
      }
    }
  }, []); // Run only once on mount

  const handleClear = useCallback(() => {
    // Wrap in useCallback if passed as prop or dependency
    setCaseData(initialCaseData);
    setDraftId(null); // Reset so next Save as Draft creates a new draft
    // Clear team sharing state
    setShareWithTeam(false);
    setSelectedTeamId("");
    setMemberSelectionType("All Members");
    setTeamMembersDetails([]);
    setSelectedMemberUids([]);
    // setUserTeams([]); // Decide if you want to clear cached teams or not
    setTeamError(null);
    // Clear local storage
    localStorage.removeItem("caseDraft");
    localStorage.removeItem("caseNotes");

    // Re-fetch teams using the function now in scope
    fetchTeams(); // <-- This call is now valid
  }, [fetchTeams]); // Dependency on the stable fetchTeams function

  // On fresh open (no location.state), load the last Firebase draft
  useEffect(() => {
    if (location.state?.draftData || location.state?.caseData) return;
    if (!globalUser) return;
    const loadLastDraft = async () => {
      try {
        const q = query(
          collection(db, "drafts"),
          where("userId", "==", globalUser.uid)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) return;
        // Pick the most recently updated draft
        let latest = null;
        snapshot.docs.forEach((d) => {
          const data = d.data();
          const t = data.updatedAt?.seconds || data.createdAt?.seconds || 0;
          if (!latest || t > (latest.data.updatedAt?.seconds || latest.data.createdAt?.seconds || 0)) {
            latest = { id: d.id, data };
          }
        });
        if (!latest) return;
        const d = { ...latest.data };
        if (d.caseFiledDate) d.caseFiledDate = new Date(d.caseFiledDate);
        if (d.previousHearingDate) d.previousHearingDate = new Date(d.previousHearingDate);
        if (d.nextHearingDate) d.nextHearingDate = new Date(d.nextHearingDate);
        if (d.stayOrderDate) d.stayOrderDate = new Date(d.stayOrderDate);
        setCaseData(d);
        setDraftId(latest.id);
        if (d._teamSharingState) {
          const ts = d._teamSharingState;
          setShareWithTeam(ts.shareWithTeam || false);
          setSelectedTeamId(ts.selectedTeamId || "");
          setMemberSelectionType(ts.memberSelectionType || "All Members");
          setSelectedMemberUids(ts.selectedMemberUids || []);
        }
      } catch (err) {
        console.error("Failed to load last draft:", err);
      }
    };
    loadLastDraft();
  }, [globalUser]); // eslint-disable-line

  // Handler for the main "Share with team" checkbox
  const handleShareToggle = () => {
    setShareWithTeam((prev) => {
      const newState = !prev;
      if (!newState) {
        // If turning sharing off, reset related states
        setSelectedTeamId("");
        setMemberSelectionType("All Members");
        setTeamMembersDetails([]);
        setSelectedMemberUids([]);
      }
      return newState;
    });
  };

  // Handler for selecting a team
  const handleTeamSelect = (e) => {
    const teamId = e.target.value;
    setSelectedTeamId(teamId);
    // Reset subsequent selections when team changes
    setMemberSelectionType("All Members");
    setSelectedMemberUids([]);
    // Fetching members is handled by the useEffect watching selectedTeamId
  };

  // Handler for selecting member type (All/Specific)
  const handleMemberSelectionTypeChange = (e) => {
    const type = e.target.value;
    setMemberSelectionType(type);
    if (type === "All Members") {
      setSelectedMemberUids([]); // Clear specific selection if 'All' is chosen
    }
  };

  // Handler for selecting/deselecting specific members
  const handleSpecificMemberToggle = (memberUid) => {
    setSelectedMemberUids((prev) => {
      if (prev.includes(memberUid)) {
        return prev.filter((uid) => uid !== memberUid); // Remove if exists
      } else {
        return [...prev, memberUid]; // Add if doesn't exist
      }
    });
  };

  const [error, setError] = useState("");
  const [userMobile, setUserMobile] = useState("");

  const userId = globalUser?.uid;

  const today = useMemo(() => {
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    return current;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setError("");
      try {
        const q = query(collection(db, "pending"), where("owner", "==", userId));
        const querySnapshot = await getDocs(q);
        const cases = querySnapshot.docs.map((doc) => doc.data().caseNo);
        //  setCaseNumbers(cases);

        const userQuery = query(
          collection(db, "lawyers"),
          where("uid", "==", userId)
        );
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          console.log("User data:", userData);
          console.log("User mobile:", userData.mobileWithCountryCode);
          setUserMobile(userData.mobileWithCountryCode || "");
        } else {
          console.warn("User document not found for UID:", userId);
          setUserMobile("");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load required data");
      }
    };
    fetchData();
  }, [userId]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (
      !caseData.status ||
      !caseData.caseNumber ||
      !caseData.caseType ||
      !caseData.caseYear ||
      !caseData.court ||
      !caseData.petitionerName ||
      !caseData.respondentName
    ) {
      toast.error("Please fill in all mandatory fields.");
      return;
    }

    // Check for duplicate case number before proceeding
    if (hasDuplicateCaseError) {
      toast.error("A case with same case number already saved");
      return;
    }

    if (!globalUser) {
      // Check globalUser from context
      toast.error("User not authenticated");
      return;
    }

    const uid = globalUser.uid; // Use UID from context

    setIsSubmitting(true);

    try {
      // Server-side duplicate check right before saving
      const pendingDupQuery = query(
        collection(db, 'pending'),
        where('owner', '==', globalUser.uid),
        where('caseNo', '==', caseData.caseNumber.trim())
      );
      const disposedDupQuery = query(
        collection(db, 'disposed'),
        where('owner', '==', globalUser.uid),
        where('caseNo', '==', caseData.caseNumber.trim())
      );
      const [pendingDupSnap, disposedDupSnap] = await Promise.all([
        getDocs(pendingDupQuery),
        getDocs(disposedDupQuery),
      ]);
      if (!pendingDupSnap.empty || !disposedDupSnap.empty) {
        toast.error("A case with this case number already exists.");
        setHasDuplicateCaseError(true);
        setDuplicateCaseErrorMessage('Case number already exists');
        setIsSubmitting(false);
        return;
      }

      console.log(caseData)
      const collectionName = getStatusCollection(caseData.status);

      // Check case limit before proceeding
      const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
        globalUser.uid,
        1,
        collectionName
      );

      if (!limitResult.canProceed) {
        toast.error(`Case limit reached! Your ${limitResult.planName} plan allows ${limitResult.maxLimit} ${collectionName} cases. Please upgrade to add more.`);
        setIsSubmitting(false);
        return;
      }

      const casesCollectionRef = collection(db, collectionName);

      console.log("case data case filled date ", caseData.caseFiledDate);
      console.log("case data next hearing date ", caseData.nextHearingDate);
      console.log(
        "case data previous hearing date ",
        caseData.previousHearingDate
      );

      const formatDate = (date) => {
        if (!date) return null;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Add 1 because getMonth() is 0-based
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const formattedCaseFiledDate = formatDate(caseData.caseFiledDate);
      const formattedNextHearingDate = formatDate(caseData.nextHearingDate);
      const formattedPreviousHearingDate = formatDate(
        caseData.previousHearingDate
      );
      const formattedStayOrderDate = formatDate(caseData.stayOrderDate);

      console.log("Formatted Case Filed Date: ", formattedCaseFiledDate);
      console.log("Formatted Next Hearing Date: ", formattedNextHearingDate);
      console.log(
        "Formatted Previous Hearing Date: ",
        formattedPreviousHearingDate
      );

      // --- Determine the final team array ---
      let finalTeamArray = [globalUser.uid]; // Owner is always included

      if (shareWithTeam && selectedTeamId) {
        if (memberSelectionType === "All Members") {
          // Add all fetched members of the selected team
          const allMemberUids = teamMembersDetails.map((member) => member.uid);
          finalTeamArray = [...new Set([...finalTeamArray, ...allMemberUids])]; // Use Set to avoid duplicates
        } else {
          // Specific Members
          // Add specifically selected members
          finalTeamArray = [
            ...new Set([...finalTeamArray, ...selectedMemberUids]),
          ]; // Use Set to avoid duplicates
        }
      }
      // --- End determining team array ---

      // Get current pending case count for subscription tracking
      let currentPendingCount = 0;
      let subscriptionMetadata = null;

      // Only count and track if we're adding to pending collection
      if (collectionName === "pending") {
        const pendingQuery = query(
          collection(db, "pending"),
          where("owner", "==", globalUser.uid)
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        currentPendingCount = pendingSnapshot.size;

        // Get subscription metadata if case is beyond free tier
        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          globalUser.uid,
          currentPendingCount
        );

        console.log('📦 Subscription metadata for new case:', {
          currentPendingCount,
          hasMetadata: !!subscriptionMetadata
        });
      }

      const caseDataToStore = {
        respondentName: caseData.respondentName,
        appearingFor: caseData.appearingFor,
        caseNo: caseData.caseNumber,
        caseType: caseData.caseType,
        caseYear: caseData.caseYear,
        casenotes: caseData.caseNotes,
        courtName:
          caseData.court === "Others"
            ? caseData.customCourt
            : caseData.specificCourt || caseData.court,
        files: caseData.files,
        caseFiledDate: formattedCaseFiledDate,
        nextHearingDate: formattedNextHearingDate,
        previousHearingDate: formattedPreviousHearingDate,
        owner: globalUser.uid, // Use UID from context
        partyContactNo: caseData.partyContactNumber,
        petitionerName: caseData.petitionerName,
        petitionerRelation: caseData.petitionerRelation || "",
        petitionerRelationName: caseData.petitionerRelationName || "",
        respondentRelation: caseData.respondentRelation || "",
        respondentRelationName: caseData.respondentRelationName || "",
        status: caseData.status,
        caseStage: caseData.stage || "",
        priority: caseData.priority || "",
        team: finalTeamArray, // Use the calculated team array
        // Organisation-group sharing: tag the case so every member of the
        // owner's case-sharing group sees it in their All Cases (read-time
        // query on sharedGroupId). First sharing group wins.
        sharedGroupId: userSharingGroups[0] || null,
        allPetitioners: caseData.allPetitioners || [],
        allRespondents: caseData.allRespondents || [],
        petitionerAdvocate: caseData.petitionerAdvocate || "",
        respondentAdvocate: caseData.respondentAdvocate || "",
        allPetitionerAdvocates: caseData.allPetitionerAdvocates || [],
        allRespondentAdvocates: caseData.allRespondentAdvocates || [],
        authorityDepartment: caseData.authorityDepartment || "",
        authorityDepartmentOther:
          caseData.authorityDepartment === "Others"
            ? caseData.authorityDepartmentOther || ""
            : "",
        properties: caseData.properties || "",
        area: caseData.area || "",
        village: caseData.village || "",
        stayOrderDate: formattedStayOrderDate,
        counterAffidavitStatus: caseData.counterAffidavitStatus || "",
        counterAppealFiled: caseData.counterAppealFiled || "",
        prayers: caseData.prayers || "",
        ...(caseData.court === "Supreme Court" && {
          payload: {
            caseexportedfrom: "SupremeCourt",
            statusStage: caseData.supremeCourtStage || "",
            category: caseData.supremeCourtCategory || "",
            section: caseData.supremeCourtSection || "",
          },
        }),
        // Add subscription metadata if present (only for cases beyond free tier)
        ...(subscriptionMetadata && { subscriptionMetadata }),
        timestamp: serverTimestamp(),
      };

    const refCase =  await addDoc(casesCollectionRef, caseDataToStore);

    console.log("Case added with ID:", refCase.id);

      // Don't save notes here, only when explicitly adding/editing notes
      // localStorage.setItem("caseNotes", caseData.caseNotes || "");

      toast.success("Case added successfully");

           // Schedule notification if hearing date exists
            // Schedule event and default reminder if next hearing date exists
if (formattedNextHearingDate) {
  const eventDate = new Date(formattedNextHearingDate.split("/").reverse().join("-"));

  // Event 1: 8:00 AM on the hearing date (visible in calendar)
  const reminderDate = new Date(eventDate);
  reminderDate.setHours(8, 0, 0, 0);

  // Event 2: 6:00 PM on the hearing date (silent - not shown in calendar)
  const eventTimeIST = new Date(eventDate);
  eventTimeIST.setHours(18, 0, 0, 0);

  // Create Event 1: Hearing reminder at 8:00 AM on hearing date
  if (reminderDate > new Date()) {
    const reminderEventData = {
      "Case No": caseData.caseNumber,
      date: reminderDate.toISOString().split("T")[0],
      id: Math.floor(Math.random() * 90000) + 10000,
      mobile: userMobile || null,
      time: "08:00 AM",
      time1: reminderDate.getTime(),
      title: `${caseData.petitionerName} VS ${caseData.respondentName}`,
      description: `Hearing Reminder : Case No. - ${caseData.caseNumber}`,
      isScheduled: reminderDate > new Date(),
      status: caseData.status,
      eventType: "reminder",
    };
    const reminderEventRef = await addDoc(collection(db, "lawyers", uid, "events"), reminderEventData);
    console.log("Event 1 (Reminder) created with ID:", reminderEventRef.id);

    const reminderDoc = {
      eventId: reminderEventRef.id,
      reminderTime: reminderDate,
      eventTitle: `Hearing Reminder : Case No. - ${caseData.caseNumber}`,
      caseNo: caseData.caseNumber,
      status: "pending",
      createdAt: new Date(),
      scheduledBy: uid,
      recipientId: uid,
    };
    await addDoc(collection(db, "eventReminders"), reminderDoc);
    console.log("Event 1 reminder added to eventReminders collection.");
  } else {
    console.log("Event 1 (Reminder) time is in the past, not creating.");
  }

  // Create Event 2: Silent update reminder at 6:00 PM on hearing date
  if (eventTimeIST > new Date()) {
    const mainEventData = {
      "Case No": caseData.caseNumber,
      date: eventDate.toISOString().split("T")[0],
      id: Math.floor(Math.random() * 90000) + 10000,
      mobile: userMobile || null,
      time: "06:00 PM",
      time1: eventTimeIST.getTime(),
      title: `Update Next Hearing: ${caseData.petitionerName} VS ${caseData.respondentName}`,
      description: `Case No. - ${caseData.caseNumber}`,
      isScheduled: eventTimeIST > new Date(),
      isHidden: true,
      status: caseData.status,
      eventType: "updateReminder",
    };
    const mainEventRef = await addDoc(collection(db, "lawyers", uid, "events"), mainEventData);
    console.log("Event 2 (Update Reminder) created with ID:", mainEventRef.id);

    const mainReminderDoc = {
      eventId: mainEventRef.id,
      reminderTime: eventTimeIST,
      eventTitle: `Update Next Hearing : ${caseData.caseNumber}`,
      caseNo: caseData.caseNumber,
      status: "pending",
      createdAt: new Date(),
      scheduledBy: uid,
      recipientId: uid,
    };
    await addDoc(collection(db, "eventReminders"), mainReminderDoc);
    console.log("Event 2 reminder added to eventReminders collection.");
  } else {
    console.log("Event 2 (Update Reminder) time is in the past, not creating.");
  }
}




      // Delete draft from Firebase if this case was created from a draft
      if (draftId) {
        try {
          await deleteDoc(doc(db, "drafts", draftId));
        } catch (e) {
          console.error("Failed to delete draft:", e);
        }
      }

      navigate("/portal/Dashboard"); // Redirect to dashboard after successful addition

      // Clear form including team state
      handleClear(); // Use handleClear to reset everything
    } catch (error) {
      console.error("Error adding case:", error);
      toast.error("Error adding case: " + error.message);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Save draft automatically (excluding meta-state)
    const draftToSave = { ...caseData };
    // Remove meta state before saving general draft if necessary
    // delete draftToSave._teamSharingState;
    if (
      Object.values(draftToSave).some(
        (value) =>
          (value !== "" &&
            value !== null &&
            value !== undefined &&
            !Array.isArray(value)) ||
          (Array.isArray(value) && value.length > 0)
      )
    ) {
      // console.log("Auto-saving draft..."); // For debugging
      // localStorage.setItem("caseDraft", JSON.stringify(draftToSave)); // Auto-save without team state? Decide requirement.
      // For now, let's only save draft explicitly with handleSaveDraft
    }
  }, [caseData]);

  return (
    <div className="min-h-full bg-[#F5F7FA]">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <div className="mb-3 flex items-center gap-1.5 text-[13px] text-slate-500">
          <Link to="/portal/Dashboard" className="hover:text-slate-700 transition-colors">Home</Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <Link to="/portal/AllCases" className="hover:text-slate-700 transition-colors">Cases</Link>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-medium text-slate-700">Add New Case</span>
        </div>

        {/* Page header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Add New Case</h1>
            <p className="mt-1 text-sm text-slate-500">
              Fill in the details below to create and manage a new case.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
            <button
              onClick={handleClear}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              Clear
            </button>
            <button
              onClick={handleSaveDraft}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              Save as Draft
            </button>
            <button
              onClick={() => navigate("/portal/ImportCases")}
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Upload className="h-4 w-4" />
              Import Case
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-yellow-500 px-5 text-sm font-semibold text-gray-900 transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {isSubmitting ? "Saving..." : "Save Case"}
            </button>
          </div>
        </div>

        {/* Workspace: form + tips rail */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-7 shadow-[0_10px_30px_rgba(16,24,40,0.06)]">
            <div className="mb-6 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-yellow-500 text-sm font-semibold text-gray-900">1</div>
              <div>
                <h2 className="text-[17px] font-semibold text-slate-900">Case Overview</h2>
                <p className="mt-0.5 text-[13px] text-slate-500">Basic information about the case</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
              {/* Status */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Case Status <span className="text-red-500">*</span>
                </label>
                <SelectMenu
                  name="status"
                  value={caseData.status}
                  onChange={handleInputChange}
                  className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                  required
                >
                  <option value="">Choose Status</option>
                  {CASE_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.value}
                    </option>
                  ))}
                </SelectMenu>
              </div>

              {/* Stage */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Case Stage
                </label>
                <SelectMenu
                  name="stage"
                  value={caseData.stage}
                  onChange={handleInputChange}
                  className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                >
                  <option value="">Choose Stage</option>
                  {CASE_STAGES.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </SelectMenu>
              </div>

              {/* Priority */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Priority
                </label>
                <SelectMenu
                  name="priority"
                  value={caseData.priority}
                  onChange={handleInputChange}
                  className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                >
                  <option value="">Choose Priority</option>
                  {CASE_PRIORITIES.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </SelectMenu>
              </div>

              {/* Case Number */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Case Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="caseNumber"
                    value={caseData.caseNumber}
                    onChange={handleInputChange}
                    onBlur={(e) => checkDuplicateCaseNumber(e.target.value)}
                    className={`w-full bg-[#0A061E] text-white p-3 rounded-lg border ${
                      hasDuplicateCaseError ? 'border-red-500' : 'border-gray-700'
                    } focus:border-${hasDuplicateCaseError ? 'red' : 'blue'}-500 focus:ring-${
                      hasDuplicateCaseError ? 'red' : 'blue'
                    }-500 pr-10`}
                    placeholder="Enter Number"
                    required
                  />
                  <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
                {isCheckingDuplicate && (
                  <p className="text-blue-400 text-xs mt-1">Checking for duplicates...</p>
                )}
                {hasDuplicateCaseError && duplicateCaseErrorMessage && (
                  <p className="text-red-500 text-xs mt-1">{duplicateCaseErrorMessage}</p>
                )}
                {!hasDuplicateCaseError && !isCheckingDuplicate && (
                  <p className="text-gray-400 text-xs mt-1">Case number cannot be changed later</p>
                )}
              </div>

              {/* Case Type */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Case Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="caseType"
                    value={caseData.caseType}
                    onChange={handleInputChange}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    placeholder="Enter Type"
                    required
                  />
                  <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Case Year */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Case Year <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DatePicker
                    selected={caseData.caseYear ? new Date(caseData.caseYear, 0, 1) : null}
                    onChange={(date) => {
                      const year = date ? date.getFullYear().toString() : "";
                      setCaseData((prev) => ({ ...prev, caseYear: year }));
                    }}
                    showYearPicker
                    dateFormat="yyyy"
                    placeholderText="Select Or Enter Year"
                    yearItemNumber={9}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    popperPlacement="bottom-start"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Court */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Court <span className="text-red-500">*</span>
                </label>
                <SelectMenu
                  name="court"
                  value={caseData.court}
                  onChange={handleInputChange}
                  className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                  required
                >
                  <option value="">Choose Court</option>
                  <option value="Supreme Court">Supreme Court</option>
                  <option value="High">High Court</option>
                  <option value="NCLT">NCLT</option>
                  <option value="RERA">RERA</option>
                  <option value="Others">Others</option>
                </SelectMenu>
              </div>

              {/* Specific Court dropdown for High Court / NCLT / RERA */}
              {(caseData.court === "High" || caseData.court === "NCLT" || caseData.court === "RERA") && (
                <div>
                  <label className="text-white mb-2 block text-sm font-medium">
                    {caseData.court === "High" ? "Select High Court" : caseData.court === "NCLT" ? "Select NCLT Bench" : "Select RERA Authority"}
                  </label>
                  <SelectMenu
                    name="specificCourt"
                    value={caseData.specificCourt}
                    onChange={handleInputChange}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                  >
                    <option value="">
                      {caseData.court === "High" ? "Choose High Court" : caseData.court === "NCLT" ? "Choose NCLT Bench" : "Choose RERA Authority"}
                    </option>
                    {(caseData.court === "High" ? HIGH_COURTS : caseData.court === "NCLT" ? NCLT_BENCHES : RERA_AUTHORITIES).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </SelectMenu>
                </div>
              )}

              {/* Custom Court - only if Others is selected */}
              {caseData.court === "Others" && (
                <div>
                  <label className="text-white mb-2 block text-sm font-medium">
                    Specify Court
                  </label>
                  <input
                    type="text"
                    name="customCourt"
                    value={caseData.customCourt}
                    onChange={(e) =>
                      setCaseData((prev) => ({
                        ...prev,
                        customCourt: e.target.value,
                      }))
                    }
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                    placeholder="Enter Name"
                  />
                </div>
              )}

              {/* Supreme Court extras - Stage, Category, Section */}
              {caseData.court === "Supreme Court" && (
                <>
                  <div>
                    <label className="text-white mb-2 block text-sm font-medium">
                      Status / Stage
                    </label>
                    <input
                      type="text"
                      name="supremeCourtStage"
                      value={caseData.supremeCourtStage}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                      placeholder="Enter Status / Stage"
                    />
                  </div>
                  <div>
                    <label className="text-white mb-2 block text-sm font-medium">
                      Category
                    </label>
                    <input
                      type="text"
                      name="supremeCourtCategory"
                      value={caseData.supremeCourtCategory}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                      placeholder="Enter Category"
                    />
                  </div>
                  <div>
                    <label className="text-white mb-2 block text-sm font-medium">
                      Section
                    </label>
                    <input
                      type="text"
                      name="supremeCourtSection"
                      value={caseData.supremeCourtSection}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                      placeholder="Enter Section"
                    />
                  </div>
                </>
              )}

              {/* Case Filed Date */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Case Filed Date
                </label>
                <div className="relative">
                  <DatePicker
                    selected={caseData.caseFiledDate}
                    onChange={handleCaseFiledDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    popperPlacement="bottom-start"
                    maxDate={new Date()}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Previous Hearing Date */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Previous Hearing Date
                </label>
                <div className="relative">
                  <DatePicker
                    selected={caseData.previousHearingDate}
                    onChange={handlePreviousHearingDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    popperPlacement="bottom-start"
                    maxDate={new Date()}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Next Hearing Date */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Next Hearing Date
                </label>
                <div className="relative">
                  <DatePicker
                    selected={caseData.nextHearingDate}
                    onChange={handleNextHearingDateChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    minDate={caseData.previousHearingDate || today}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    popperPlacement="bottom-start"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Party Contact Number */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Party Contact Number
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="partyContactNumber"
                    value={caseData.partyContactNumber}
                    onChange={handleInputChange}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    placeholder="Enter 10-Digit Number"
                    pattern="\d{10}"
                    title="Please enter a valid 10-digit phone number."
                  />
                  <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Authority Department (gated by department-access verification) */}
              {isDepartmentVerified && (
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Authority Department
                </label>
                <CustomSelect
                  value={caseData.authorityDepartment}
                  onChange={(val) =>
                    setCaseData((prev) => ({
                      ...prev,
                      authorityDepartment: val,
                      ...(val !== "Land" ? { village: "" } : {}),
                      ...(val !== "Others" ? { authorityDepartmentOther: "" } : {}),
                    }))
                  }
                  options={AUTHORITY_DEPARTMENTS}
                  placeholder="Choose Department"
                />
              </div>
              )}

              {isDepartmentVerified && caseData.authorityDepartment === "Others" && (
                <div>
                  <label className="text-white mb-2 block text-sm font-medium">
                    Please specify authority department
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="authorityDepartmentOther"
                      value={caseData.authorityDepartmentOther}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                      placeholder="Enter Department"
                    />
                    <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>
              )}

              {isDepartmentVerified && caseData.authorityDepartment && (
                <>
                  {/* Properties */}
                  <div>
                    <label className="text-white mb-2 block text-sm font-medium">
                      Properties
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="properties"
                        value={caseData.properties}
                        onChange={handleInputChange}
                        className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                        placeholder="Enter Properties"
                      />
                      <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                    </div>
                  </div>

                  {/* Area */}
                  <div>
                    <label className="text-white mb-2 block text-sm font-medium">
                      Area
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="area"
                        value={caseData.area}
                        onChange={handleInputChange}
                        className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                        placeholder="Enter Area"
                      />
                      <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                    </div>
                  </div>
                </>
              )}

              {/* Village (only if Land) */}
              {isDepartmentVerified && caseData.authorityDepartment === "Land" && (
                <div>
                  <label className="text-white mb-2 block text-sm font-medium">
                    Village
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="village"
                      value={caseData.village}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                      placeholder="Enter Village"
                    />
                    <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>
              )}

              {/* Stay Order Date */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Stay Order Date
                </label>
                <div className="relative">
                  <DatePicker
                    selected={caseData.stayOrderDate}
                    onChange={(date) =>
                      setCaseData((prev) => ({ ...prev, stayOrderDate: date }))
                    }
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    popperPlacement="bottom-start"
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Status of Counter Affidavit */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Status of Counter Affidavit
                </label>
                <CustomSelect
                  value={caseData.counterAffidavitStatus}
                  onChange={(val) =>
                    setCaseData((prev) => ({
                      ...prev,
                      counterAffidavitStatus: val,
                    }))
                  }
                  options={COUNTER_AFFIDAVIT_STATUSES}
                  placeholder="Choose Status"
                />
              </div>

             <div className="flex flex-col gap-2">
  <label className="text-white text-sm font-medium">
    Counter Appeal Filed
  </label>

  <div className="flex items-center gap-6">
    <label className="inline-flex items-center gap-2 cursor-pointer text-white">
      <input
        type="radio"
        name="counterAppealFiled"
        value="Yes"
        checked={caseData.counterAppealFiled === "Yes"}
        onChange={() =>
          setCaseData((prev) => ({ ...prev, counterAppealFiled: "Yes" }))
        }
        className="accent-yellow-500"
      />
      <span>Yes</span>
    </label>

    <label className="inline-flex items-center gap-2 cursor-pointer text-white">
      <input
        type="radio"
        name="counterAppealFiled"
        value="No"
        checked={caseData.counterAppealFiled === "No"}
        onChange={() =>
          setCaseData((prev) => ({ ...prev, counterAppealFiled: "No" }))
        }
        className="accent-yellow-500"
      />
      <span>No</span>
    </label>
  </div>
</div>

            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-7 shadow-[0_10px_30px_rgba(16,24,40,0.06)]">
            <div className="mb-6 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-yellow-500 text-sm font-semibold text-gray-900">2</div>
              <div>
                <h2 className="text-[17px] font-semibold text-slate-900">Parties &amp; Representation</h2>
                <p className="mt-0.5 text-[13px] text-slate-500">Add parties and their legal representatives</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
              {/* Appearing For */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Appearing For
                </label>
                <SelectMenu
                  name="appearingFor"
                  value={caseData.appearingFor}
                  onChange={handleInputChange}
                  className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400"
                >
                  <option value="">Choose One</option>
                  <option value="petitioner">Petitioner</option>
                  <option value="respondent">Respondent</option>
                </SelectMenu>
              </div>
              {/* Petitioner */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Petitioner Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="petitionerName"
                    value={caseData.petitionerName}
                    onChange={handleInputChange}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    placeholder="Enter Name"
                    required
                  />
                  <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                  {caseData.allPetitioners.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">+{caseData.allPetitioners.length} Petitioners</span>
                      <button
                        type="button"
                        onClick={() => setShowPetitionerModal(true)}
                        className="text-xs bg-yellow-50 text-yellow-700 hover:bg-yellow-100 px-2 py-0.5 rounded transition-colors"
                      >
                        View
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowPetitionerModal(true)}
                      className="text-sm text-yellow-700 hover:text-yellow-700 transition-colors whitespace-nowrap"
                    >
                      + Add More
                    </button>
                  )}
                  <RelationBadge
                    relation={caseData.petitionerRelation}
                    relationName={caseData.petitionerRelationName}
                    onOpen={() => setShowPetitionerRelationDialog(true)}
                    onClear={() => setCaseData(prev => ({ ...prev, petitionerRelation: "", petitionerRelationName: "" }))}
                  />
                </div>
              </div>

              {/* Petitioner Advocate */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Petitioner Advocate
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="petitionerAdvocate"
                    value={caseData.petitionerAdvocate}
                    onChange={handleInputChange}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    placeholder="Enter Name"
                  />
                  <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
                {caseData.allPetitionerAdvocates.length > 0 ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-slate-600">+{caseData.allPetitionerAdvocates.length} Petitioner Advocates</span>
                    <button
                      type="button"
                      onClick={() => setShowPetitionerAdvocateModal(true)}
                      className="text-xs bg-yellow-50 text-yellow-700 hover:bg-yellow-100 px-2 py-0.5 rounded transition-colors"
                    >
                      View
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowPetitionerAdvocateModal(true)}
                    className="mt-2 text-sm text-yellow-700 hover:text-yellow-700 transition-colors"
                  >
                    + Add More Petitioner Advocates
                  </button>
                )}
              </div>

              {/* Respondent */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Respondent Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="respondentName"
                    value={caseData.respondentName}
                    onChange={handleInputChange}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    placeholder="Enter Name"
                    required
                  />
                  <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                  {caseData.allRespondents.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">+{caseData.allRespondents.length} Respondents</span>
                      <button
                        type="button"
                        onClick={() => setShowRespondentModal(true)}
                        className="text-xs bg-yellow-50 text-yellow-700 hover:bg-yellow-100 px-2 py-0.5 rounded transition-colors"
                      >
                        View
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowRespondentModal(true)}
                      className="text-sm text-yellow-700 hover:text-yellow-700 transition-colors whitespace-nowrap"
                    >
                      + Add More
                    </button>
                  )}
                  <RelationBadge
                    relation={caseData.respondentRelation}
                    relationName={caseData.respondentRelationName}
                    onOpen={() => setShowRespondentRelationDialog(true)}
                    onClear={() => setCaseData(prev => ({ ...prev, respondentRelation: "", respondentRelationName: "" }))}
                  />
                </div>
              </div>

              {/* Respondent Advocate */}
              <div>
                <label className="text-white mb-2 block text-sm font-medium">
                  Respondent Advocate
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="respondentAdvocate"
                    value={caseData.respondentAdvocate}
                    onChange={handleInputChange}
                    className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-10"
                    placeholder="Enter Name"
                  />
                  <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
                {caseData.allRespondentAdvocates.length > 0 ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-slate-600">+{caseData.allRespondentAdvocates.length} Respondent Advocates</span>
                    <button
                      type="button"
                      onClick={() => setShowRespondentAdvocateModal(true)}
                      className="text-xs bg-yellow-50 text-yellow-700 hover:bg-yellow-100 px-2 py-0.5 rounded transition-colors"
                    >
                      View
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowRespondentAdvocateModal(true)}
                    className="mt-2 text-sm text-yellow-700 hover:text-yellow-700 transition-colors"
                  >
                    + Add More Respondent Advocates
                  </button>
                )}
              </div>

              {/* Prayers */}
              <div className="md:col-span-2">
                <label className="text-white mb-2 block text-sm font-medium">
                  Prayers
                </label>
                <textarea
                  name="prayers"
                  value={caseData.prayers}
                  onChange={handleInputChange}
                  rows={1}
                  className="w-full bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 resize-y whitespace-pre-wrap break-words"
                  placeholder="Enter Reliefs Sought"
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-7 shadow-[0_10px_30px_rgba(16,24,40,0.06)]">
            <div className="mb-6 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-yellow-500 text-sm font-semibold text-gray-900">3</div>
              <div>
                <h2 className="text-[17px] font-semibold text-slate-900">Collaboration &amp; Files</h2>
                <p className="mt-0.5 text-[13px] text-slate-500">Share access with team and upload relevant documents</p>
              </div>
            </div>
            {/* --- Team Sharing Section --- */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                multiple
                className="hidden"
              />

              {/* Share with team members row (toggles the options below) */}
              <button
                type="button"
                onClick={handleShareToggle}
                className="mb-4 flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left transition-colors hover:border-slate-300"
              >
                <span className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-[6px] border transition-colors ${
                      shareWithTeam ? "border-yellow-500 bg-yellow-500" : "border-slate-300 bg-white"
                    }`}
                  >
                    {shareWithTeam && <Check className="h-3.5 w-3.5 text-gray-900" />}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Share with team members</span>
                    <span className="block text-[13px] text-slate-500">Allow selected team members to view and collaborate on this case</span>
                  </span>
                </span>
                <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${shareWithTeam ? "rotate-90" : ""}`} />
              </button>

              {/* Drag & drop / upload */}
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer?.files?.length) {
                    handleFileUpload({ target: { files: e.dataTransfer.files } });
                  }
                }}
                className="cursor-pointer rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/70 px-6 py-8 text-center transition-colors hover:border-yellow-400 hover:bg-yellow-50/40"
              >
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm">
                  <UploadCloud className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-slate-600">
                  <span>Drag and drop files here or</span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700">
                    <Upload className="h-3.5 w-3.5" /> Upload File
                  </span>
                </div>
                <p className="mt-2 text-[12px] text-slate-400">Supported formats: PDF, DOC, DOCX, ZIP (Max 25MB)</p>
              </div>

              {caseData.files.length > 0 && (
                <p className="mt-3 text-[13px] font-medium text-slate-600">
                  {caseData.files.length} file{caseData.files.length > 1 ? "s" : ""} attached
                </p>
              )}

              {shareWithTeam && (
                <div className="space-y-6 pl-8">
                  {" "}
                  {/* Indent options */}
                  {/* Select Team Dropdown */}
                  <div>
                    <label className="text-white mb-2 block text-sm font-medium">
                      Select Team
                    </label>
                    <div className="relative">
                      <SelectMenu chevron={false}
                        value={selectedTeamId}
                        onChange={handleTeamSelect}
                        className="w-full appearance-none bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-8" // Added pr-8 for icon space
                        disabled={loadingTeams}
                      >
                        <option value="">
                          {loadingTeams
                            ? "Loading teams..."
                            : userTeams.length > 0
                              ? "Choose a team"
                              : "No teams found"}
                        </option>
                        {userTeams
                          .filter((team) => !team.isDefault)
                          .map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.teamName}
                          </option>
                        ))}
                      </SelectMenu>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                    </div>
                    {teamError && (
                      <p className="text-red-500 text-xs mt-1">{teamError}</p>
                    )}
                  </div>
                  {/* Select Members Type Dropdown (only if a team is selected) */}
                  {selectedTeamId && (
                    <div>
                      <label className="text-white mb-2 block text-sm font-medium">
                        Share with
                      </label>
                      <div className="relative">
                        <SelectMenu chevron={false}
                          value={memberSelectionType}
                          onChange={handleMemberSelectionTypeChange}
                          className="w-full appearance-none bg-[#0A061E] text-white p-3 rounded-xl border border-slate-200 focus:border-yellow-400 focus:ring-yellow-400 pr-8"
                          disabled={loadingTeamMembers} // Disable while loading members
                        >
                          <option value="All Members">All Members</option>
                          <option value="Specific Members">
                            Specific Members
                          </option>
                        </SelectMenu>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                      </div>
                    </div>
                  )}
                  {/* Specific Members Checkbox List */}
                  {selectedTeamId &&
                    memberSelectionType === "Specific Members" && (
                      <div>
                        <label className="text-white mb-2 block text-sm font-medium">
                          Select Specific Members
                        </label>
                        <div className="w-full bg-slate-50 text-slate-700 p-3 rounded-xl border border-slate-200 max-h-48 overflow-y-auto space-y-3">
                          {" "}
                          {/* Scrollable container */}
                          {loadingTeamMembers && (
                            <div className="flex items-center justify-center text-gray-400">
                              <Loader2 className="animate-spin mr-2" />
                              Loading members...
                            </div>
                          )}
                          {!loadingTeamMembers &&
                            teamMembersDetails.length === 0 && (
                              <p className="text-gray-400 text-center">
                                No members found in this team.
                              </p>
                            )}
                          {!loadingTeamMembers &&
                            teamMembersDetails.map((member) => (
                              <CustomCheckbox
                                key={member.uid}
                                label={
                                  member.name ||
                                  `User ${member.uid.substring(0, 5)}`
                                }
                                checked={selectedMemberUids.includes(
                                  member.uid
                                )}
                                onChange={() =>
                                  handleSpecificMemberToggle(member.uid)
                                }
                              />
                            ))}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
            {/* --- End Team Sharing Section --- */}
            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-6">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-yellow-500 px-6 text-sm font-semibold text-gray-900 transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                {isSubmitting ? "Saving..." : "Save Case"}
              </button>
              <button
                onClick={handleSaveDraft}
                className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                Save as Draft
              </button>
            </div>
          </div>{" "}
          {/* End Collaboration card */}
          </div>{" "}
          {/* End left column */}

          {/* Tips rail */}
          <aside className="h-fit rounded-2xl bg-white border border-slate-200 p-6 shadow-[0_10px_30px_rgba(16,24,40,0.06)] xl:sticky xl:top-4">
            <div className="mb-2 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100">
                <Lightbulb className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-slate-900">Tips for adding case</h3>
                <p className="mt-0.5 text-[12px] text-slate-500">Quick guidance while creating a case.</p>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {[
                ["Case Number", "Once saved, the case number cannot be edited."],
                ["Status & Stage", "Choose the correct status and stage to track progress accurately."],
                ["Dates", "Filing date is used to calculate timelines and deadlines."],
                ["Parties", "Add all parties and advocates to enable collaboration."],
                ["Save as Draft", "You can save the case as draft and continue later."],
              ].map(([t, d]) => (
                <div key={t} className="flex gap-2.5 py-3.5">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-500" />
                  <div>
                    <p className="text-[13px] font-semibold text-slate-800">{t}</p>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-500">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>{" "}
        {/* End workspace grid */}
      </div>

      {/* Add More Modals */}
      <AddMoreModal
        isOpen={showPetitionerModal}
        onClose={() => setShowPetitionerModal(false)}
        title="Add More Petitioners"
        items={caseData.allPetitioners}
        setItems={(items) => setCaseData(prev => ({ ...prev, allPetitioners: items }))}
        supportsRelation
      />
      <AddMoreModal
        isOpen={showRespondentModal}
        onClose={() => setShowRespondentModal(false)}
        title="Add More Respondents"
        items={caseData.allRespondents}
        setItems={(items) => setCaseData(prev => ({ ...prev, allRespondents: items }))}
        supportsRelation
      />
      <AddMoreModal
        isOpen={showPetitionerAdvocateModal}
        onClose={() => setShowPetitionerAdvocateModal(false)}
        title="Add More Petitioner Advocates"
        items={caseData.allPetitionerAdvocates}
        setItems={(items) => setCaseData(prev => ({ ...prev, allPetitionerAdvocates: items }))}
      />
      <AddMoreModal
        isOpen={showRespondentAdvocateModal}
        onClose={() => setShowRespondentAdvocateModal(false)}
        title="Add More Respondent Advocates"
        items={caseData.allRespondentAdvocates}
        setItems={(items) => setCaseData(prev => ({ ...prev, allRespondentAdvocates: items }))}
      />

      <RelationDialog
        isOpen={showPetitionerRelationDialog}
        onClose={() => setShowPetitionerRelationDialog(false)}
        title={`Set Relation${caseData.petitionerName ? ` — ${caseData.petitionerName}` : ""}`}
        relation={caseData.petitionerRelation}
        relationName={caseData.petitionerRelationName}
        onSave={({ relation, relationName }) =>
          setCaseData(prev => ({ ...prev, petitionerRelation: relation, petitionerRelationName: relationName }))
        }
        namePlaceholder="Enter father/mother name"
      />
      <RelationDialog
        isOpen={showRespondentRelationDialog}
        onClose={() => setShowRespondentRelationDialog(false)}
        title={`Set Relation${caseData.respondentName ? ` — ${caseData.respondentName}` : ""}`}
        relation={caseData.respondentRelation}
        relationName={caseData.respondentRelationName}
        onSave={({ relation, relationName }) =>
          setCaseData(prev => ({ ...prev, respondentRelation: relation, respondentRelationName: relationName }))
        }
        namePlaceholder="Enter father/mother name"
      />
    </div>
  );
}

export default AddCase;
